import * as React from 'react'
import {
    Container,
    Grid2,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography,
    IconButton,
    Autocomplete,
    Chip,
    Divider,
    MenuItem,
    Button,
    Box,
    Collapse,
    Tooltip,
    Tabs,
    Tab,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import PrintIcon from '@mui/icons-material/Print'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import EditIcon from '@mui/icons-material/Edit'
import AssessmentIcon from '@mui/icons-material/Assessment'
import Navbar from '../setup/Navbar'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'
import Loading from '../../BuiltInComponent/Loading'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
    getItemsWithStock,
    getIssuances,
    getDepartments,
    saveManualIssuance,
    updateManualIssuance,
    resetProductionState
} from '../../features/AdminManagement/productionSlice'
import { printManualIssuanceReport } from '../../utils/manualIssuancePrint'
import { branchList as getBranchList } from '../../features/AdminManagement/branchSlice'

const localDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const todayDate = () => localDate(new Date())
const monthStartDate = () => {
    const today = new Date()
    return localDate(new Date(today.getFullYear(), today.getMonth(), 1))
}

const EMPTY_ROW = { itemCode: '', itemDesc: '', quantity: '', inUnit: '', currentStock: 0 }

export default function ManualIssuance() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { itemsWithStock, issuances, departments, isLoading } = useSelector(s => s.production)
    const activeBranches = useSelector(s => s.branches.masterData || [])
    const { user } = useSelector(s => s.auth)

    const [branchID, setBranchID] = React.useState('')
    const [issuanceType, setIssuanceType] = React.useState('manual')
    const [departmentID, setDepartmentID] = React.useState('')
    const [remarks, setRemarks] = React.useState('')
    const [rows, setRows] = React.useState([{ ...EMPTY_ROW }])
    const [historyPage, setHistoryPage] = React.useState(0)
    const [historyRowsPerPage, setHistoryRowsPerPage] = React.useState(10)
    const [expandedIssuance, setExpandedIssuance] = React.useState(null)
    const [editingIssuanceID, setEditingIssuanceID] = React.useState(null)
    const itemInputRefs = React.useRef([])
    const pendingItemFocusIndex = React.useRef(null)

    // History filters
    const [hFilter, setHFilter] = React.useState({
        branchID: '',
        departmentID: '',
        fromDate: monthStartDate(),
        toDate: todayDate(),
        issuanceType: 'manual',
        itemCode: ''
    })

    React.useEffect(() => {
        dispatch(getBranchList({ getOrder: true }))
        dispatch(getDepartments())
        // eslint-disable-next-line
    }, [dispatch])

    React.useEffect(() => {
        if (!activeBranches.length || branchID) return
        const firstBranchID = Number(activeBranches[0].branchID)
        setBranchID(firstBranchID)
        setHFilter(prev => ({ ...prev, branchID: firstBranchID }))
        dispatch(getItemsWithStock({ branchID: firstBranchID }))
        dispatch(getIssuances({ branchID: firstBranchID, fromDate: hFilter.fromDate, toDate: hFilter.toDate, issuanceType }))
    }, [activeBranches, branchID, dispatch, hFilter.fromDate, hFilter.toDate, issuanceType])

    const onTypeChange = (_, nextType) => {
        setIssuanceType(nextType)
        setHFilter(prev => ({ ...prev, issuanceType: nextType }))
        setExpandedIssuance(null)
        dispatch(getIssuances({
            branchID: hFilter.branchID || branchID || undefined,
            departmentID: hFilter.departmentID || undefined,
            fromDate: hFilter.fromDate || undefined,
            toDate: hFilter.toDate || undefined,
            issuanceType: nextType,
            itemCode: hFilter.itemCode || undefined
        }))
    }

    const onBranchChange = (val) => {
        setBranchID(val)
        setHFilter(prev => ({ ...prev, branchID: val }))
        dispatch(getItemsWithStock({ branchID: val }))
    }

    // ---- item rows ----
    const onItemSelect = (index, selectedItem) => {
        if (!selectedItem) {
            const updated = [...rows]
            updated[index] = { ...EMPTY_ROW }
            setRows(updated)
            return
        }
        const updated = [...rows]
        updated[index] = {
            itemCode: selectedItem.itemCode,
            itemDesc: selectedItem.itemDesc,
            inUnit: selectedItem.inUnit,
            currentStock: selectedItem.currentStock,
            quantity: ''
        }
        setRows(updated)
    }

    const onQtyChange = (index, val) => {
        const updated = [...rows]
        updated[index] = { ...updated[index], quantity: val }
        setRows(updated)
    }

    const addRow = () => {
        pendingItemFocusIndex.current = rows.length
        setRows(prev => [...prev, { ...EMPTY_ROW }])
    }

    React.useEffect(() => {
        const index = pendingItemFocusIndex.current
        if (index === null) return
        pendingItemFocusIndex.current = null
        window.setTimeout(() => itemInputRefs.current[index]?.focus(), 0)
    }, [rows.length])

    const removeRow = (index) => {
        if (rows.length === 1) { setRows([{ ...EMPTY_ROW }]); return }
        setRows(prev => prev.filter((_, i) => i !== index))
    }

    const cancelEdit = () => {
        setEditingIssuanceID(null)
        const firstBranchID = Number(activeBranches[0]?.branchID) || ''
        setBranchID(firstBranchID)
        setDepartmentID('')
        setRemarks('')
        setRows([{ ...EMPTY_ROW }])
        setIssuanceType('manual')
        setHFilter(prev => ({ ...prev, branchID: firstBranchID, issuanceType: 'manual' }))
        if (firstBranchID) dispatch(getItemsWithStock({ branchID: firstBranchID }))
    }

    const editIssuance = (issuance) => {
        const editBranchID = Number(issuance.branchID) || 1
        const editIssuanceType = ['wastage', 'discard'].includes(issuance.issuanceType) ? issuance.issuanceType : 'manual'
        setEditingIssuanceID(issuance.issuanceID)
        setIssuanceType(editIssuanceType)
        setHFilter(prev => ({ ...prev, branchID: editBranchID, issuanceType: editIssuanceType }))
        setBranchID(editBranchID)
        setDepartmentID(issuance.departmentID || '')
        setRemarks(issuance.remarks || '')
        setRows(issuance.details.map(detail => {
            const stockItem = itemsWithStock.find(item => Number(item.itemCode) === Number(detail.itemCode))
            return {
                itemCode: detail.itemCode,
                itemDesc: detail.itemDesc,
                quantity: Number(detail.quantity),
                inUnit: detail.inUnit,
                currentStock: Number(stockItem?.currentStock || 0) + Number(detail.quantity || 0)
            }
        }))
        dispatch(getItemsWithStock({ branchID: editBranchID }))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // ---- Save ----
    const onSave = async () => {
        if (!departmentID) {
            alert('Department select karen. Har department ki separate issuance save hogi.')
            return
        }
        const validItems = rows.filter(r => r.itemCode && Number(r.quantity) > 0)
        if (validItems.length === 0) {
            alert('Kam az kam ek item aur quantity add karen')
            return
        }
        try {
            const payload = {
                branchID,
                issuanceType,
                departmentID: departmentID || null,
                remarks,
                issuedBy: user?.userID || null,
                items: validItems.map(r => ({ itemCode: r.itemCode, quantity: Number(r.quantity) }))
            }
            const result = editingIssuanceID
                ? await dispatch(updateManualIssuance({ issuanceID: editingIssuanceID, ...payload })).unwrap()
                : await dispatch(saveManualIssuance(payload)).unwrap()
            alert(`Issuance #${result.issuanceID || result.referenceID} ${editingIssuanceID ? 'updated' : 'saved'} successfully!`)
            setEditingIssuanceID(null)
            setRows([{ ...EMPTY_ROW }])
            setRemarks('')
            setDepartmentID('')
            await dispatch(getItemsWithStock({ branchID }))
            await dispatch(getIssuances({
                branchID: hFilter.branchID || undefined,
                departmentID: hFilter.departmentID || undefined,
                fromDate: hFilter.fromDate || undefined,
                toDate: hFilter.toDate || undefined,
                issuanceType,
                itemCode: hFilter.itemCode || undefined
            }))
            dispatch(resetProductionState())
        } catch (error) {
            alert('Error: ' + error)
        }
    }

    // ---- History search ----
    const onHistorySearch = () => {
        dispatch(getIssuances({
            branchID: hFilter.branchID || undefined,
            departmentID: hFilter.departmentID || undefined,
            fromDate: hFilter.fromDate || undefined,
            toDate: hFilter.toDate || undefined,
            issuanceType,
            itemCode: hFilter.itemCode || undefined
        }))
    }

    const formatDate = (dt) => dt ? new Date(dt).toLocaleString('en-PK') : ''

    const issuanceMasters = React.useMemo(() => {
        const grouped = new Map()
        issuances.forEach(detail => {
            const id = detail.issuanceID || detail.referenceID
            if (!grouped.has(id)) grouped.set(id, { ...detail, issuanceID: id, details: [] })
            grouped.get(id).details.push(detail)
        })
        return Array.from(grouped.values())
    }, [issuances])

    const paginatedIssuances = React.useMemo(() => {
        const start = historyPage * historyRowsPerPage
        return issuanceMasters.slice(start, start + historyRowsPerPage)
    }, [issuanceMasters, historyPage, historyRowsPerPage])

    React.useEffect(() => {
        setHistoryPage(0)
    }, [issuances])

    return (
        <>
            <Navbar />
            <Container maxWidth="xl">
                <Grid2 container spacing={2} sx={{ mt: 2, mb: 4 }}>

                    {/* Heading */}
                    <Grid2 size={10}>
                        <HeadingText text="Manual Inventory Issuance" />
                    </Grid2>
                    <Grid2 size={2} sx={{ textAlign: 'right' }}>
                        <Tooltip title='Current Stock Report'>
                            <IconButton color='primary' size='large' sx={{ p: 1.25 }} onClick={() => navigate('/CurrentStock')}>
                                <AssessmentIcon fontSize='large' />
                            </IconButton>
                        </Tooltip>
                    </Grid2>
                    <Grid2 size={12}>
                        <Paper variant="outlined" sx={{ px: 2 }}>
                            <Tabs value={issuanceType} onChange={onTypeChange} textColor="primary" indicatorColor="primary">
                                <Tab value="manual" label="Manual Issuance" />
                                <Tab value="wastage" label="Wastage" />
                                <Tab value="discard" label="Discard" />
                            </Tabs>
                        </Paper>
                    </Grid2>

                    {/* ---- Top controls ---- */}
                    <Grid2 size={3}>
                        <TextField
                            select fullWidth size="small" label="Branch"
                            value={branchID}
                            onChange={e => onBranchChange(Number(e.target.value))}
                        >
                            {activeBranches.map(branch => (
                                <MenuItem key={branch.branchID} value={branch.branchID}>{branch.branchName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={3}>
                        <TextField
                            select fullWidth size="small" label="Department"
                            value={departmentID}
                            onChange={e => setDepartmentID(e.target.value)}
                        >
                            <MenuItem value="">-- Select Department --</MenuItem>
                            {departments.map(d => (
                                <MenuItem key={d.departmentID} value={d.departmentID}>{d.departmentName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={6}>
                        <TextField
                            fullWidth size="small" label="Remarks (Optional)"
                            value={remarks}
                            onChange={e => setRemarks(e.target.value)}
                        />
                    </Grid2>

                    {/* ---- Items Table ---- */}
                    <Grid2 size={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {editingIssuanceID ? `Edit ${issuanceType === 'manual' ? 'Issuance' : issuanceType === 'discard' ? 'Discard' : 'Wastage'} #${editingIssuanceID}` : `${issuanceType === 'manual' ? 'Issue' : issuanceType === 'discard' ? 'Discard' : 'Wastage'} Items`}
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#780202' }}>
                                            <TableCell sx={{ color: '#fff', width: '40%' }}>Item</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '15%' }} align="center">Current Stock</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '10%' }} align="center">Unit</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '20%' }} align="center">Issue Quantity</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '15%' }} align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={itemsWithStock}
                                                        getOptionLabel={o => `${o.itemDesc}`}
                                                        value={itemsWithStock.find(i => i.itemCode === row.itemCode) || null}
                                                        onChange={(_, val) => onItemSelect(idx, val)}
                                                        renderInput={params => <TextField {...params} inputRef={(element) => { itemInputRefs.current[idx] = element }} placeholder="Item search..." />}
                                                        isOptionEqualToValue={(o, v) => o.itemCode === v.itemCode}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={Number(row.currentStock).toFixed(3)}
                                                        size="small"
                                                        color={row.currentStock > 0 ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">{row.inUnit}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <TextField
                                                        size="small" type="number"
                                                        inputProps={{ min: 0, step: 0.001 }}
                                                        value={row.quantity}
                                                        onChange={e => onQtyChange(idx, e.target.value)}
                                                        sx={{ width: 120 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Grid2 container spacing={2} sx={{ mt: 1 }}>
                                <Grid2 size={6}>
                                    <ButtonNormal
                                        text="+ Add Row"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={addRow}
                                        variant="outlined"
                                    />
                                </Grid2>
                                <Grid2 size={6} sx={{ textAlign: 'right' }}>
                                    {editingIssuanceID && (
                                        <Button variant="outlined" color="inherit" onClick={cancelEdit} sx={{ mr: 1 }}>
                                            Cancel Edit
                                        </Button>
                                    )}
                                    {isLoading ? <Loading /> :
                                        <ButtonNormal text={editingIssuanceID ? 'Update Issuance' : 'Save Issuance'} onClick={onSave} />
                                    }
                                </Grid2>
                            </Grid2>
                        </Paper>
                    </Grid2>

                    {/* ---- Divider ---- */}
                    <Grid2 size={12}>
                        <Divider sx={{ my: 1 }} />
                        <Grid2 container alignItems="center" justifyContent="space-between" spacing={2}>
                            <Typography variant="h6" fontWeight="bold">{issuanceType === 'manual' ? 'Issuance' : issuanceType === 'discard' ? 'Discard' : 'Wastage'} History</Typography>
                            <Button variant="contained" startIcon={<PrintIcon />} disabled={!issuances.length}
                                onClick={() => printManualIssuanceReport(issuances, hFilter, user)}>
                                Print Report
                            </Button>
                        </Grid2>
                    </Grid2>

                    {/* ---- History Filters ---- */}
                    <Grid2 size={2}>
                        <TextField
                            select fullWidth size="small" label="Department"
                            value={hFilter.departmentID}
                            onChange={e => setHFilter(p => ({ ...p, departmentID: e.target.value }))}
                        >
                            <MenuItem value="">All Departments</MenuItem>
                            {departments.map(d => (
                                <MenuItem key={d.departmentID} value={d.departmentID}>{d.departmentName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            select fullWidth size="small" label="Branch"
                            value={hFilter.branchID}
                            onChange={e => setHFilter(p => ({ ...p, branchID: Number(e.target.value) }))}
                        >
                            <MenuItem value="">All</MenuItem>
                            {activeBranches.map(branch => (
                                <MenuItem key={branch.branchID} value={branch.branchID}>{branch.branchName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            fullWidth size="small" type="date" label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={hFilter.fromDate}
                            onChange={e => setHFilter(p => ({ ...p, fromDate: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            fullWidth size="small" type="date" label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={hFilter.toDate}
                            onChange={e => setHFilter(p => ({ ...p, toDate: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Autocomplete
                            size="small"
                            options={itemsWithStock}
                            getOptionLabel={o => o.itemDesc}
                            value={itemsWithStock.find(i => i.itemCode === hFilter.itemCode) || null}
                            onChange={(_, val) => setHFilter(p => ({ ...p, itemCode: val ? val.itemCode : '' }))}
                            renderInput={params => <TextField {...params} label="Item (Optional)" />}
                            isOptionEqualToValue={(o, v) => o.itemCode === v.itemCode}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <ButtonNormal text="Search" onClick={onHistorySearch} />
                    </Grid2>
                    <Grid2 size={1} />

                    {/* ---- History Table ---- */}
                    <Grid2 size={12}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ width: 48 }} />
                                        <TableCell><b>Issuance #</b></TableCell>
                                        <TableCell><b>Date / Time</b></TableCell>
                                        <TableCell><b>Branch</b></TableCell>
                                        <TableCell><b>Department</b></TableCell>
                                        <TableCell align="center"><b>Total Items</b></TableCell>
                                        <TableCell><b>Issued By</b></TableCell>
                                        <TableCell><b>Remarks</b></TableCell>
                                        <TableCell align="center"><b>Edit</b></TableCell>
                                        <TableCell align="center"><b>Print</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {issuances.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                    No records found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedIssuances.map(r => {
                                        const isOpen = expandedIssuance === r.issuanceID
                                        return <React.Fragment key={r.issuanceID}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => setExpandedIssuance(isOpen ? null : r.issuanceID)}>
                                                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{r.issuanceID}</TableCell>
                                                <TableCell>{formatDate(r.recordedAt)}</TableCell>
                                                <TableCell>{r.branchName}</TableCell>
                                                <TableCell>{r.departmentName}</TableCell>
                                                <TableCell align="center">{r.details.length}</TableCell>
                                                <TableCell>{r.issuedBy}</TableCell>
                                                <TableCell>{r.remarks}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="primary" onClick={() => editIssuance(r)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Print this issuance">
                                                        <IconButton size="small" color="primary" onClick={() => printManualIssuanceReport(
                                                            r.details,
                                                            {
                                                                singleIssuance: true,
                                                                issuanceID: r.issuanceID,
                                                                branchName: r.branchName,
                                                                departmentName: r.departmentName,
                                                                recordedAt: r.recordedAt,
                                                                remarks: r.remarks
                                                            },
                                                            user
                                                        )}>
                                                            <PrintIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ py: 0 }} colSpan={10}>
                                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                        <Box sx={{ py: 1.5, px: 4 }}>
                                                            <Typography variant="subtitle2" gutterBottom>Issuance Detail</Typography>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Item</TableCell>
                                                                        <TableCell align="right">Issued Quantity</TableCell>
                                                                        <TableCell align="right">Balance After</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>{r.details.map(d => (
                                                                    <TableRow key={d.ledgerID}>
                                                                        <TableCell>{d.itemDesc}</TableCell>
                                                                        <TableCell align="right">{Number(d.quantity).toFixed(3)} {d.inUnit}</TableCell>
                                                                        <TableCell align="right">{Number(d.balanceAfter).toFixed(3)}</TableCell>
                                                                    </TableRow>
                                                                ))}</TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={issuanceMasters.length}
                            page={historyPage}
                            onPageChange={(_, newPage) => setHistoryPage(newPage)}
                            rowsPerPage={historyRowsPerPage}
                            onRowsPerPageChange={(event) => {
                                setHistoryRowsPerPage(parseInt(event.target.value, 10))
                                setHistoryPage(0)
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Rows per page:"
                        />
                    </Grid2>

                </Grid2>
            </Container>
        </>
    )
}
